import { openDB } from 'idb';

class LocalStorageService {
  constructor() {
    this.dbName = 'CaravaneSante';
    this.dbVersion = 1;
    this.db = null;
    this.init();
  }

  async init() {
    try {
      this.db = await openDB(this.dbName, this.dbVersion, {
        upgrade(db) {
          // Store pour les patients
          if (!db.objectStoreNames.contains('patients')) {
            const patientStore = db.createObjectStore('patients', { keyPath: 'id' });
            patientStore.createIndex('medicalRecordNumber', 'medicalRecordNumber', { unique: true });
            patientStore.createIndex('lastName', 'lastName');
            patientStore.createIndex('firstName', 'firstName');
          }

          // Store pour les consultations
          if (!db.objectStoreNames.contains('consultations')) {
            const consultationStore = db.createObjectStore('consultations', { keyPath: 'id' });
            consultationStore.createIndex('patientId', 'patient_id');
            consultationStore.createIndex('consultationDate', 'consultation_date');
          }

          // Store pour les constantes vitales
          if (!db.objectStoreNames.contains('vitals')) {
            const vitalsStore = db.createObjectStore('vitals', { keyPath: 'id' });
            vitalsStore.createIndex('patientId', 'patient_id');
            vitalsStore.createIndex('measurementDate', 'measurement_date');
          }

          // Store pour les traitements
          if (!db.objectStoreNames.contains('treatments')) {
            const treatmentsStore = db.createObjectStore('treatments', { keyPath: 'id' });
            treatmentsStore.createIndex('patientId', 'patient_id');
            treatmentsStore.createIndex('startDate', 'start_date');
          }

          // Store pour les antécédents médicaux
          if (!db.objectStoreNames.contains('medicalHistory')) {
            const historyStore = db.createObjectStore('medicalHistory', { keyPath: 'id' });
            historyStore.createIndex('patientId', 'patient_id');
            historyStore.createIndex('recordDate', 'record_date');
          }

          // Store pour la queue de synchronisation
          if (!db.objectStoreNames.contains('syncQueue')) {
            const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
            syncStore.createIndex('type', 'type');
            syncStore.createIndex('status', 'status');
            syncStore.createIndex('timestamp', 'timestamp');
          }

          // Store pour le statut de synchronisation
          if (!db.objectStoreNames.contains('syncStatus')) {
            db.createObjectStore('syncStatus', { keyPath: 'id' });
          }
        },
      });
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de la base de données locale:', error);
    }
  }

  // ===== GESTION DES PATIENTS =====
  async savePatientLocally(patient) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('patients', 'readwrite');
      await tx.objectStore('patients').put({
        ...patient,
        lastModified: new Date().toISOString(),
        isLocal: true
      });
      await tx.done;
      
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('patient', 'create', patient);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde locale du patient:', error);
      return false;
    }
  }

  async getLocalPatients() {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('patients', 'readonly');
      const patients = await tx.objectStore('patients').getAll();
      await tx.done;
      return patients;
    } catch (error) {
      console.error('Erreur lors de la récupération des patients locaux:', error);
      return [];
    }
  }

  async updatePatientLocally(patient) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('patients', 'readwrite');
      await tx.objectStore('patients').put({
        ...patient,
        lastModified: new Date().toISOString(),
        isLocal: true
      });
      await tx.done;
      
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('patient', 'update', patient);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour locale du patient:', error);
      return false;
    }
  }

  // ===== GESTION DES CONSULTATIONS =====
  async saveConsultationLocally(consultation) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('consultations', 'readwrite');
      await tx.objectStore('consultations').put({
        ...consultation,
        lastModified: new Date().toISOString(),
        isLocal: true
      });
      await tx.done;
      
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('consultation', 'create', consultation);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde locale de la consultation:', error);
      return false;
    }
  }

  async getLocalConsultations(patientId = null) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('consultations', 'readonly');
      let consultations;
      
      if (patientId) {
        consultations = await tx.objectStore('consultations').index('patientId').getAll(patientId);
      } else {
        consultations = await tx.objectStore('consultations').getAll();
      }
      
      await tx.done;
      return consultations;
    } catch (error) {
      console.error('Erreur lors de la récupération des consultations locales:', error);
      return [];
    }
  }

  // ===== GESTION DES CONSTANTES VITALES =====
  async saveVitalsLocally(vitals) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('vitals', 'readwrite');
      await tx.objectStore('vitals').put({
        ...vitals,
        lastModified: new Date().toISOString(),
        isLocal: true
      });
      await tx.done;
      
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('vitals', 'create', vitals);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde locale des constantes vitales:', error);
      return false;
    }
  }

  async getLocalVitals(patientId = null) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('vitals', 'readonly');
      let vitals;
      
      if (patientId) {
        vitals = await tx.objectStore('vitals').index('patientId').getAll(patientId);
      } else {
        vitals = await tx.objectStore('vitals').getAll();
      }
      
      await tx.done;
      return vitals;
    } catch (error) {
      console.error('Erreur lors de la récupération des constantes vitales locales:', error);
      return [];
    }
  }

  // ===== GESTION DES TRAITEMENTS =====
  async saveTreatmentLocally(treatment) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('treatments', 'readwrite');
      await tx.objectStore('treatments').put({
        ...treatment,
        lastModified: new Date().toISOString(),
        isLocal: true
      });
      await tx.done;
      
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('treatment', 'create', treatment);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde locale du traitement:', error);
      return false;
    }
  }

  async getLocalTreatments(patientId = null) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('treatments', 'readonly');
      let treatments;
      
      if (patientId) {
        treatments = await tx.objectStore('treatments').index('patientId').getAll(patientId);
      } else {
        treatments = await tx.objectStore('treatments').getAll();
      }
      
      await tx.done;
      return treatments;
    } catch (error) {
      console.error('Erreur lors de la récupération des traitements locaux:', error);
      return [];
    }
  }

  // ===== GESTION DES ANTÉCÉDENTS MÉDICAUX =====
  async saveMedicalHistoryLocally(history) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('medicalHistory', 'readwrite');
      await tx.objectStore('medicalHistory').put({
        ...history,
        lastModified: new Date().toISOString(),
        isLocal: true
      });
      await tx.done;
      
      // Ajouter à la queue de synchronisation
      await this.addToSyncQueue('medicalHistory', 'create', history);
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde locale de l\'antécédent médical:', error);
      return false;
    }
  }

  async getLocalMedicalHistory(patientId = null) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('medicalHistory', 'readonly');
      let history;
      
      if (patientId) {
        history = await tx.objectStore('medicalHistory').index('patientId').getAll(patientId);
      } else {
        history = await tx.objectStore('medicalHistory').getAll();
      }
      
      await tx.done;
      return history;
    } catch (error) {
      console.error('Erreur lors de la récupération des antécédents médicaux locaux:', error);
      return [];
    }
  }

  // ===== GESTION DE LA QUEUE DE SYNCHRONISATION =====
  async addToSyncQueue(type, operation, data) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('syncQueue', 'readwrite');
      await tx.objectStore('syncQueue').add({
        type,
        operation,
        data,
        status: 'pending',
        timestamp: new Date().toISOString(),
        retryCount: 0
      });
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de l\'ajout à la queue de synchronisation:', error);
    }
  }

  async getSyncQueue() {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('syncQueue', 'readonly');
      const queue = await tx.objectStore('syncQueue').getAll();
      await tx.done;
      return queue;
    } catch (error) {
      console.error('Erreur lors de la récupération de la queue de synchronisation:', error);
      return [];
    }
  }

  async removeFromSyncQueue(id) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('syncQueue', 'readwrite');
      await tx.objectStore('syncQueue').delete(id);
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la suppression de la queue de synchronisation:', error);
    }
  }

  async updateSyncQueueStatus(id, status, error = null) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('syncQueue', 'readwrite');
      const item = await tx.objectStore('syncQueue').get(id);
      if (item) {
        item.status = status;
        item.lastAttempt = new Date().toISOString();
        if (error) {
          item.error = error;
          item.retryCount = (item.retryCount || 0) + 1;
        }
        await tx.objectStore('syncQueue').put(item);
      }
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de la queue:', error);
    }
  }

  // ===== GESTION DU STATUT DE SYNCHRONISATION =====
  async updateSyncStatus(status) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('syncStatus', 'readwrite');
      await tx.objectStore('syncStatus').put({
        id: 'main',
        ...status,
        lastUpdate: new Date().toISOString()
      });
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de synchronisation:', error);
    }
  }

  async getSyncStatus() {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('syncStatus', 'readonly');
      const status = await tx.objectStore('syncStatus').get('main');
      await tx.done;
      return status || { isOnline: false, lastSync: null, pendingItems: 0 };
    } catch (error) {
      console.error('Erreur lors de la récupération du statut de synchronisation:', error);
      return { isOnline: false, lastSync: null, pendingItems: 0 };
    }
  }

  // ===== SAUVEGARDE EN LOT =====
  async savePatientsLocally(patients) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('patients', 'readwrite');
      const store = tx.objectStore('patients');
      
      for (const patient of patients) {
        await store.put(patient);
      }
      
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des patients:', error);
    }
  }

  async saveConsultationsLocally(consultations) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('consultations', 'readwrite');
      const store = tx.objectStore('consultations');
      
      for (const consultation of consultations) {
        await store.put(consultation);
      }
      
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des consultations:', error);
    }
  }

  async saveVitalsLocally(vitals) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('vitals', 'readwrite');
      const store = tx.objectStore('vitals');
      
      for (const vital of vitals) {
        await store.put(vital);
      }
      
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des constantes vitales:', error);
    }
  }

  async saveTreatmentsLocally(treatments) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('treatments', 'readwrite');
      const store = tx.objectStore('treatments');
      
      for (const treatment of treatments) {
        await store.put(treatment);
      }
      
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des traitements:', error);
    }
  }

  async saveMedicalHistoryLocally(medicalHistory) {
    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction('medicalHistory', 'readwrite');
      const store = tx.objectStore('medicalHistory');
      
      for (const history of medicalHistory) {
        await store.put(history);
      }
      
      await tx.done;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des antécédents:', error);
    }
  }

  // ===== NETTOYAGE =====
  async clearAllData() {
    if (!this.db) await this.init();
    
    try {
      const stores = ['patients', 'consultations', 'vitals', 'treatments', 'medicalHistory', 'syncQueue', 'syncStatus'];
      
      for (const storeName of stores) {
        const tx = this.db.transaction(storeName, 'readwrite');
        await tx.objectStore(storeName).clear();
        await tx.done;
      }
    } catch (error) {
      console.error('Erreur lors du nettoyage des données:', error);
    }
  }

}

// Instance singleton
const localStorageService = new LocalStorageService();
export default localStorageService;
